import { useCallback, type ReactNode } from 'react'

import RichText from '@/components/ui/rich-text'
import Link from '@/components/link-component'
import ImageContentful from '@/components/ui/image-contentful'
import Image from '@/components/ui/image'

import { cx } from '@/utils/strings'
import { type TypeComponentQuoteSkeleton } from '@/types/ctf/TypeComponentQuote'
import type { Entry } from 'contentful'
import colors from '@/constants/colors'
import CtaLink from './cta-link'

type QuoteEntry = Entry<TypeComponentQuoteSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

const { gold, teal, violet, melon } = colors

export default function Quote({
  actions,
  attribution,
  body,
  borderColor = 'Violet',
  image,
  quoteImage,
  link,
  className,
}: QuoteEntry['fields']) {
  const LinkWrap = useCallback(
    ({ children, to }: { children: ReactNode; to?: string }) => (
      <>
        {to ? (
          <Link className="w-full" to={to}>
            {children}
          </Link>
        ) : (
          children
        )}
      </>
    ),
    []
  )

  return (
    <LinkWrap to={link?.fields && link.fields.url}>
      <figure
        className={cx(
          'p-6 mb-1 rounded-lg bg-white dark:bg-secondary-800 border-b-2 @container',
          borderColor === violet && 'border-b-violet-500',
          borderColor === teal && 'border-b-teal-500',
          borderColor === gold && 'border-b-gold-500',
          borderColor === melon && 'border-b-melon-500',
          className
        )}
      >
        <blockquote className="[&_p]:text-xl [&_p]:sm:text-2xl [&_p]:lg:text-2xl @3xl:[&_p]:lg:text-4xl leading-normal font-normal inline-block [&_p:last-child]:mb-0 ">
          <RichText body={body} />
        </blockquote>
        {quoteImage && (
          <div className={cx('my-6 w-[150px] aspect-video')}>
            <Image {...quoteImage.fields} />
          </div>
        )}
        {image && !quoteImage && (
          <div className={cx('my-6 w-[150px] aspect-video')}>
            <ImageContentful loading="eager" {...image.fields} className="[&_svg]:fill-inherit" />
          </div>
        )}
        <figcaption className={cx('font-heading text-xl [&_cite]:not-italic [&_cite]:block mt-4 mb-1')}>
          <cite>{attribution}</cite>
        </figcaption>
        {actions && (
          <div className="flex flex-row gap-6 px-2">
            {actions.map((action) => (
              <CtaLink key={action?.sys.id} {...action} to={action.fields.link.fields.url}>
                {action.fields.label}
              </CtaLink>
            ))}
          </div>
        )}
      </figure>
    </LinkWrap>
  )
}
